import { DeviceEventType, reportDeviceEvents } from '@/common/api/omReport'
import { SystemResourceInfo } from './monitor/type'
import { loganLog } from '@RootCommon/utils/logan'
import { checkNetwork } from '../refreshPage'

const NETWORK_DISCONNECT_START_KEY = 'network_disconnect_start_time'

/**
 * 设备事件收集器
 * 负责收集和上报设备相关的事件和数据:
 * - 系统休眠/唤醒事件
 * - 网络断开/恢复事件
 * - 系统资源信息(CPU、内存)
 * - 应用关闭时间
 */

/**
 * 上报休眠事件
 */
async function reportSleepEvent (startTime: number, endTime: number) {
  try {
    await reportDeviceEvents(DeviceEventType.SYSTEM_SLEEP, startTime, endTime)
  } catch (error) {
    console.error('休眠事件上报失败', error)
  }
}

/**
 * 上报网络断开事件
 */
export async function reportNetworkDisconnectEvent (startTime: number, endTime: number) {
  try {
    await reportDeviceEvents(DeviceEventType.NETWORK_DISCONNECT, startTime, endTime)
  } catch (error) {
    console.error('断网事件上报失败', error)
  }
}

/**
 * 启动系统休眠检测
 * 通过监听 electron 的 powerMonitor 事件来检测系统休眠/唤醒
 */
export function startSleepDetection () {
  const onPowerMonitor = window.electronAPI?.onPowerMonitor
  if (!onPowerMonitor) {
    return
  }

  const listener = async (event: string) => {
    if (event === 'resume') {
      // 系统从休眠状态唤醒
      try {
        const windowsStore = window.electronAPI?.store
        if (windowsStore) {
          const systemMonitorData = await windowsStore.get('systemMonitor')
          const { awake_time, sleep_time } = systemMonitorData || {}

          if (sleep_time && awake_time) {
            // 上报休眠事件: sleep_time 为休眠开始时间, awake_time 为唤醒时间
            await reportSleepEvent(sleep_time, awake_time)
          }
        }
      } catch (error) {
        console.error('获取系统休眠信息失败', error)
      }
    }
  }

  onPowerMonitor(listener)
}

/**
 * 保存断网开始时间到 sessionStorage
 */
function saveDisconnectTime (time: number) {
  try {
    sessionStorage.setItem(NETWORK_DISCONNECT_START_KEY, String(time))
  } catch (error) {
    console.error('保存断网时间失败', error)
  }
}

/**
 * 获取断网开始时间
 */
function getDisconnectTime (): number | null {
  try {
    const time = sessionStorage.getItem(NETWORK_DISCONNECT_START_KEY)
    return time ? Number(time) : null
  } catch (error) {
    console.error('获取断网时间失败', error)
    return null
  }
}

/**
 * 清除断网开始时间
 */
function clearDisconnectTime () {
  try {
    sessionStorage.removeItem(NETWORK_DISCONNECT_START_KEY)
  } catch (error) {
    console.error('清除断网时间失败', error)
  }
}

/**
 * 上报断网事件并清理记录
 */
async function reportAndClearDisconnect (startTime: number, endTime: number) {
  try {
    await reportNetworkDisconnectEvent(startTime, endTime)
    clearDisconnectTime()
  } catch (error) {
    console.error('上报断网事件失败', error)
  }
}

/**
 * 页面加载时检查是否有未上报的断网事件
 * 如果有断网记录且当前网络正常，则上报
 */
export async function checkAndReportDisconnectOnLoad () {
  const disconnectStartTime = getDisconnectTime()

  // 如果没有断网记录，直接返回
  if (!disconnectStartTime) {
    return
  }

  // 检查当前网络是否正常
  const isOnline = navigator.onLine
  if (!isOnline) {
    console.log('页面加载时仍有断网记录，当前网络仍断开')
    return
  }

  // 验证网络是否真正可用
  const isReallyConnected = await checkNetwork()
  if (isReallyConnected) {
    // 网络正常，上报断网事件
    const endTime = Date.now()
    await reportAndClearDisconnect(disconnectStartTime, endTime)
  } else {
    console.log('页面加载时网络连通性验证失败，保留断网记录')
  }
}

/**
 * 启动网络断开检测
 * 通过监听浏览器的 offline/online 事件来检测网络状态变化
 * 在 online 事件中增加实际网络连通性检测，避免网卡显示已连接但实际不通的情况
 * 支持页面刷新后继续记录断网时长
 * @returns 清理函数
 */
export function startNetworkDisconnectDetection (): () => void {
  let isChecking = false // 防止并发检查

  // 页面加载时检查未上报的断网事件
  checkAndReportDisconnectOnLoad()

  // 监听网络断开事件
  const offlineListener = () => {
    const existingTime = getDisconnectTime()
    // 只在没有断网记录时才保存，避免覆盖
    if (!existingTime) {
      saveDisconnectTime(Date.now())
    }
  }
  window.addEventListener('offline', offlineListener)

  // 监听网络恢复事件
  const onlineListener = async () => {
    const disconnectStartTime = getDisconnectTime()

    // 如果没有断网记录，或者正在检查中，直接返回
    if (!disconnectStartTime || isChecking) {
      return
    }

    isChecking = true

    try {
      // 验证网络是否真正可用
      const isReallyConnected = await checkNetwork()

      if (isReallyConnected) {
        // 网络真正恢复，上报断网事件
        const endTime = Date.now()
        await reportAndClearDisconnect(disconnectStartTime, endTime)
      }
    } catch (error) {
      console.error('网络连通性检测异常', error)
    } finally {
      isChecking = false
    }
  }
  window.addEventListener('online', onlineListener)

  // 返回清理函数
  return () => {
    window.removeEventListener('offline', offlineListener)
    window.removeEventListener('online', onlineListener)
  }
}

/**
 * 获取系统资源信息
 * @returns 系统资源信息或null(如果不支持)
 */
export async function getSystemResourceInfo (): Promise<{
  cpuUsage: number
  memoryUsage: number
} | null> {
  const systemResourceFn = window.electronAPI?.getSystemCpuMemoryInfo
  if (!systemResourceFn) {
    return null
  }

  try {
    const info = (await systemResourceFn()) as SystemResourceInfo
    // 使用系统CPU和内存占用率的平均值
    const cpuUsage = info.systemCpuUsage[0] // [平均值, tp50, tp75, tp90]
    const memoryUsage = info.systemMemoryUsageRate[0]

    return {
      cpuUsage,
      memoryUsage,
    }
  } catch (error) {
    console.error('获取系统资源信息失败', error)
    return null
  }
}

/**
 * 获取上次应用关闭时间
 * @returns 上次关闭时间戳或null
 */
export async function getLastShutdownTime (): Promise<number | null> {
  const windowsStore = window.electronAPI?.store
  if (!windowsStore) {
    return null
  }

  try {
    const appMonitorData = await windowsStore.get('appMonitor')
    const { app_close_time } = appMonitorData || {}
    return app_close_time || null
  } catch (error) {
    console.error('获取上次关闭时间失败', error)
    return null
  }
}
